import { useContent } from "contexts/contentContext/contentContext";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import Button from "components/Elements/Button/Button";
import Icon from "components/Elements/Icon/Icon";
import AnimationSection from "../AnimationSection/AnimationSection";
import { useSearchParams } from "react-router-dom";
import apiRequest from "utils/ApiRequest/apiRequest";
import { useToasts } from "react-toast-notifications";
import loader from "helpers/loaderUrl";

import styles from "../thanksActionModals.module.scss";

const checkMark = "https://resources.jumbomail.me/assets/images/checkmark.svg";

interface ThanksFormModalI {
  trId: string;
}

const ThanksFormModal = memo(({ trId }: ThanksFormModalI) => {
  const { addToast } = useToasts();
  const { getTextByKey } = useContent();
  const [searchParams] = useSearchParams();
  const transactionIdPayPal = searchParams.get("t");

  const [transactionId, setTransactionId] = useState(
    trId || transactionIdPayPal
  );

  useEffect(() => {
    if (!transactionId && transactionIdPayPal) {
      setTransactionId(transactionIdPayPal);
    }
  }, [transactionIdPayPal, transactionId]);


  const [formData, setFormData] = useState({ message: "" });
  const [isSubmit, setIsSubmit] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const [errors, setErrors] = useState<{ message: boolean }>({
    message: false,
  });

  const [messageError, setMessageError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (!value.trim()) {
      if (name === "message") {
        setMessageError(getTextByKey("field-cannot-be-empty"));
        setIsInvalid(true);
        setErrors((prevErrors) => ({ ...prevErrors, message: true }));
      }
      setErrors((prevErrors) => ({ ...prevErrors, [name]: true }));
    } else {
      if (name === "message") {
        setMessageError("");
      }
      setErrors((prevErrors) => ({ ...prevErrors, [name]: false }));
      setIsInvalid(false);
    }

    // Automatic height adjustment
    if (textareaRef.current) {
      // Reset height to auto to correctly adjust to new content
      textareaRef.current.style.height = "auto";
      // Set the new height based on the scrollHeight (content height)
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      if (!value) {
        if (name === "message") {
          setMessageError(getTextByKey("field-cannot-be-empty"));
          setIsInvalid(true);
          setErrors((prevErrors) => ({ ...prevErrors, message: true }));
        }
        setErrors((prevErrors) => ({ ...prevErrors, [name]: true }));
      } else if (name === "message" && value.trim()) {
        setMessageError(getTextByKey(""));
        setErrors((prevErrors) => ({ ...prevErrors, message: false }));
        setIsInvalid(false);
      }
    },
    [getTextByKey]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!formData.message.trim()) {
        setErrors({ message: true });
        setMessageError(getTextByKey("field-cannot-be-empty"));
        setIsInvalid(true);
        return;
      }

      setIsLoading(true);
      const response = await apiRequest(
        "Billing",
        "Feedback",
        {
          TransactionId: transactionId,
          Message: formData.message,
        },
        "POST"
      );

      if (response.status === "success") {
        setIsSubmit(!isSubmit);
        setIsLoading(false);
        setIsSuccess(true);
      } else {
        addToast("send-feedback-error", {
          appearance: "error",
        });
        setIsLoading(false);
        setIsSuccess(false);
      }
    },
    [addToast, formData.message, getTextByKey, isSubmit, transactionId]
  );

  const downloadInvoice = async () => {
    setIsDownloadingInvoice(true);
    const response = await apiRequest(
      "Transactions",
      "Invoice",
      {
        transactionId: transactionId,
      },
      "GET"
    );

    if (response.status === "success") {
      window.open(response.data, "_blank");
    }
    setIsDownloadingInvoice(false);
  };

  return (
    <>
      <AnimationSection />
      <div className={`${styles.modal} ${styles.thanksFormModal}`}>
        <div className={styles.modalWrap}>
          <div className={styles.modalTextWrap}>
            <h5>{getTextByKey("thank-you-for-support")}</h5>
            <p>{getTextByKey("contribution-thanks-message")}</p>
          </div>
          <form
            onSubmit={handleSubmit}
            className={`${styles.form} form-container`}
            noValidate
          >
            {isSubmit ? (
              <p>{getTextByKey("thank-you-for-feedback")}</p>
            ) : (
              <>
                <div
                  className={`${styles.formGroup} ${
                    errors.message ? styles.error : ""
                  }`}
                >
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder=""
                    required
                    ref={textareaRef}
                    className={styles.formTextarea}
                    maxLength={250}
                  />

                  <label htmlFor="message">
                    {getTextByKey("thank-you-message")}
                  </label>
                  {messageError && (
                    <div className={styles.errorMessage}>{messageError}</div>
                  )}
                </div>
                {isInvalid && (
                  <div className="invalid-notification">
                    <Icon name="warning" red />
                    {getTextByKey("default-input-invalid-message")}
                  </div>
                )}
              </>
            )}
            <div className={styles.formBtnWrap}>
              <>
                <Button
                  type="submit"
                  disabled={isLoading || isSuccess}
                  img={{
                    src: isLoading ? loader : isSuccess ? checkMark : null,
                    title: isLoading
                      ? "loader"
                      : isSuccess
                      ? "checkMark"
                      : null,
                  }}
                  className={styles.submitButton}
                >
                  {getTextByKey("send")}
                </Button>
                {!isSubmit && (
                  <Button
                    type="button"
                    onClick={downloadInvoice}
                    buttonClass={styles.invoiceButton}
                  >
                    {getTextByKey("invoice-here")}
                  </Button>
                )}
              </>
            </div>
          </form>
        </div>
      </div>
    </>
  );
});

export default ThanksFormModal;
